// v2
// Path: frontend/src/components/DataCanvasV3/CanvasFileItem.jsx
// Inputs: { instanceId, filePath, extension, templateType, templateConfig, connectionPrefix, position, size, token, onUpdate, onRemove }
// Outputs: Rendered file canvas item (image viewer or metadata card) with resize grab handles
// Summary: Renders a file dropped onto the Data Canvas using a DB-backed display template. Supports IMAGE_VIEWER (inline resizable image) and METADATA_CARD (compact info card) template types. Position and size are persisted in canvas config via onUpdate callbacks.

import React, { useState, useRef, useCallback, useEffect } from 'react';

const RESIZE_HANDLE_SIZE = 10;

// Format bytes to human-readable
function formatBytes(bytes) {
    if (bytes == null || bytes === '') return '—';
    const b = Number(bytes);
    if (isNaN(b)) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// Format ISO date to readable string
function formatDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch (_) { return iso; }
}

const CanvasFileItem = ({
    instanceId,
    filePath,
    extension,
    fileName,
    fileSize,
    fileDateModified,
    templateType,
    templateConfig,
    connectionPrefix,
    position,
    size,
    token,
    onUpdate,
    onRemove,
    isStructureMode,
}) => {
    const cfg = typeof templateConfig === 'string' ? (() => { try { return JSON.parse(templateConfig); } catch (_) { return {}; } })() : (templateConfig || {});
    const defaultW = cfg.defaultWidth || 320;
    const defaultH = cfg.defaultHeight || 240;
    const minW = cfg.minWidth || 80;
    const minH = cfg.minHeight || 60;

    const [w, setW] = useState(size?.width || defaultW);
    const [h, setH] = useState(size?.height || defaultH);
    const [imgError, setImgError] = useState(false);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [blobUrl, setBlobUrl] = useState(null);

    // Fetch the image as a blob URL so we can attach the JWT Authorization header
    useEffect(() => {
        if (templateType !== 'IMAGE_VIEWER') return;
        let revoked = false;
        let url = null;
        const controller = new AbortController();
        const load = async () => {
            try {
                const params = new URLSearchParams({ path: filePath });
                if (connectionPrefix) params.set('targetPath', connectionPrefix.replace('[', '').replace(']', ''));
                const resp = await fetch(`/api/v3/file-content?${params.toString()}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                    signal: controller.signal,
                });
                if (!resp.ok) { if (!revoked) setImgError(true); return; }
                const blob = await resp.blob();
                if (revoked) return;
                url = URL.createObjectURL(blob);
                setBlobUrl(url);
                setImgLoaded(true);
            } catch (e) {
                if (!revoked) setImgError(true);
            }
        };
        load();
        return () => {
            revoked = true;
            controller.abort();
            if (url) URL.revokeObjectURL(url);
        };
    }, [filePath, connectionPrefix, token, templateType]);

    // Resize drag state
    const resizingRef = useRef(null);
    const itemRef = useRef(null);

    const onResizeMouseDown = useCallback((e, corner) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startW = w;
        const startH = h;
        resizingRef.current = { startX, startY, startW, startH, corner };

        const onMove = (mv) => {
            if (!resizingRef.current) return;
            const { startX, startY, startW, startH, corner } = resizingRef.current;
            const dx = mv.clientX - startX;
            const dy = mv.clientY - startY;
            let newW = startW;
            let newH = startH;
            if (corner.includes('e')) newW = Math.max(minW, startW + dx);
            if (corner.includes('w')) newW = Math.max(minW, startW - dx);
            if (corner.includes('s')) newH = Math.max(minH, startH + dy);
            if (corner.includes('n')) newH = Math.max(minH, startH - dy);
            setW(newW);
            setH(newH);
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            if (resizingRef.current) {
                onUpdate && onUpdate(instanceId, { size: { width: Math.round(w), height: Math.round(h) } });
            }
            resizingRef.current = null;
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [w, h, instanceId, onUpdate, minW, minH]);

    // Commit size on mouseup via effect after state settles
    useEffect(() => {
        // No-op — onUp inside the closure handles persistence
    }, []);

    const baseName = fileName || filePath.split(/[\\/]/).pop();

    return (
        <div
            ref={itemRef}
            style={{
                position: 'relative',
                display: 'inline-block',
                width: w,
                userSelect: 'none',
            }}
            className="canvas-file-item group"
        >
            {/* Remove button */}
            {isStructureMode && (
                <button
                    onClick={() => onRemove && onRemove(instanceId)}
                    title="Remove file item"
                    style={{
                        position: 'absolute', top: -10, right: -10, zIndex: 10,
                        width: 20, height: 20, borderRadius: '50%',
                        background: '#ef4444', color: '#fff', border: 'none',
                        cursor: 'pointer', fontSize: 11, fontWeight: 'bold',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >✕</button>
            )}

            {templateType === 'IMAGE_VIEWER' ? (
                <div style={{
                    width: w, height: h,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid rgba(99,102,241,0.3)',
                    background: '#0f172a',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
                    position: 'relative',
                }}>
                    {/* Header bar */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 8px', background: 'rgba(15,23,42,0.9)',
                        borderBottom: '1px solid rgba(99,102,241,0.2)',
                    }}>
                        <span style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: w - 40 }}>
                            🖼 {baseName}
                        </span>
                        <span style={{ fontSize: 10, color: '#475569', flexShrink: 0, marginLeft: 4 }}>{(extension || '').toUpperCase()}</span>
                    </div>

                    {/* Image body */}
                    <div style={{ width: '100%', height: h - 27, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
                        {imgError ? (
                            <div style={{ textAlign: 'center', color: '#64748b' }}>
                                <div style={{ fontSize: 28 }}>🖼</div>
                                <div style={{ fontSize: 11, marginTop: 4 }}>Image unavailable</div>
                                <div style={{ fontSize: 10, marginTop: 2, maxWidth: 200, wordBreak: 'break-all', color: '#475569' }}>{baseName}</div>
                            </div>
                        ) : (
                            <img
                                src={blobUrl}
                                alt={baseName}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.2s' }}
                                draggable={false}
                            />
                        )}
                        {!imgLoaded && !imgError && (
                            <div style={{ position: 'absolute', color: '#475569', fontSize: 11 }}>Loading…</div>
                        )}
                    </div>

                    {/* Resize handles - corners */}
                    {['nw','ne','sw','se'].map(corner => (
                        <div
                            key={corner}
                            onMouseDown={(e) => onResizeMouseDown(e, corner)}
                            style={{
                                position: 'absolute',
                                width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE,
                                background: 'rgba(99,102,241,0.8)',
                                borderRadius: 2,
                                cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                                ...(corner.includes('n') ? { top: 0 } : { bottom: 0 }),
                                ...(corner.includes('w') ? { left: 0 } : { right: 0 }),
                                opacity: 0,
                                transition: 'opacity 0.15s',
                            }}
                            className="resize-handle"
                        />
                    ))}
                    {/* Show handles on hover via inline style group trick */}
                    <style>{`.canvas-file-item:hover .resize-handle { opacity: 1 !important; }`}</style>
                </div>
            ) : (
                /* METADATA_CARD */
                <div style={{
                    width: w, minHeight: h,
                    borderRadius: 8,
                    border: '1px solid rgba(99,102,241,0.25)',
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                    padding: '10px 14px',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>
                        {extension === '.pdf' ? '📄' : extension === '.txt' ? '📝' : extension === '.csv' ? '📊' : '📁'}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 8, wordBreak: 'break-all' }}>{baseName}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', fontSize: 11 }}>
                        <span style={{ color: '#64748b' }}>Extension</span><span style={{ color: '#94a3b8' }}>{extension || '—'}</span>
                        <span style={{ color: '#64748b' }}>Size</span><span style={{ color: '#94a3b8' }}>{formatBytes(fileSize)}</span>
                        <span style={{ color: '#64748b' }}>Modified</span><span style={{ color: '#94a3b8' }}>{formatDate(fileDateModified)}</span>
                    </div>
                    {/* Resize handle - bottom right corner only for card */}
                    <div
                        onMouseDown={(e) => onResizeMouseDown(e, 'se')}
                        style={{
                            position: 'absolute', bottom: 2, right: 2,
                            width: 12, height: 12, cursor: 'nwse-resize',
                            borderRight: '2px solid rgba(99,102,241,0.5)',
                            borderBottom: '2px solid rgba(99,102,241,0.5)',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default CanvasFileItem;
